import type { Maybe } from "graphql/jsutils/Maybe";
import { addedDiff } from "./addedDiff";
import { deletedDiff } from "./deletedDiff";
import { updatedDiff } from "./updatedDiff";

type ObjectWithNestedProperties<T> = {
  [K in keyof T]: T[K] extends Record<string, unknown>
    ? ObjectWithNestedProperties<T[K]>
    : T[K];
};

function mergeObjects<T extends Record<string, unknown>>(
  ...objects: Partial<NonNullable<T>>[]
): ObjectWithNestedProperties<T> {
  if (objects.length === 0) {
    return {} as ObjectWithNestedProperties<T>;
  }

  if (objects.length === 1) {
    return { ...objects[0] } as ObjectWithNestedProperties<T>;
  }

  const result = { ...objects[0] } as ObjectWithNestedProperties<T>;

  for (let i = 1; i < objects.length; i++) {
    const currentObj = objects[i];

    for (const key in currentObj) {
      if (Object.prototype.hasOwnProperty.call(currentObj, key)) {
        const val1 = result[key];
        const val2 = currentObj[key];

        if (typeof val2 === "object" && typeof val1 === "object") {
          result[key as keyof typeof currentObj] = mergeObjects(
            val1 as Record<string, unknown>,
            val2 as Record<string, unknown>
          ) as ObjectWithNestedProperties<T>[keyof T];
        } else {
          result[key as keyof typeof currentObj] =
            val2 as ObjectWithNestedProperties<T>[keyof T];
        }
      }
    }
  }

  return result;
}

export function mergeDiffs<T>(
  lhs: Maybe<Record<string, unknown>> = {},
  rhs: Record<string, unknown> = {},
  keysToOmitFromDiff?: (string | ((key: string) => boolean))[]
) {
  const added = addedDiff(lhs, rhs, keysToOmitFromDiff);
  const updated = updatedDiff(lhs, rhs, keysToOmitFromDiff);
  const deleted = deletedDiff(lhs, rhs, keysToOmitFromDiff);

  return mergeObjects(added, updated, deleted) as Partial<T>;
}
